import React from "react";
import { Link, useLocation } from "react-router-dom";

import {
  primaryNavItems,
  sidebarAccountNavItems,
  sidebarNavIconSlotClass,
  navItemIsActive,
} from "../../config/nav";
import MoreMenu from "./MoreMenu";
import MailboxDropdown from "./MailboxDropdown";
import { getInitialSidebarCollapsed, persistSidebarCollapsed } from "./sidebarStorage";
import { useMailboxScope } from "../../contexts/MailboxScopeContext";

export { getInitialSidebarCollapsed, persistSidebarCollapsed };

function sidebarUserInitial(currentUser) {
  const full = (currentUser?.identity?.full_name || "").trim();
  const email = (currentUser?.identity?.email || "").trim();
  const base = full || email || "?";
  const ch = base.charAt(0);
  return ch ? ch.toUpperCase() : "?";
}

function SidebarUserAvatar({ currentUser }) {
  const avatarUrl = (currentUser?.identity?.avatar_url || "").trim();
  const initial = sidebarUserInitial(currentUser);
  const discClass =
    "h-6 w-6 rounded-full border border-theme-border bg-theme-panel overflow-hidden object-cover";

  return (
    <span className={sidebarNavIconSlotClass}>
      {avatarUrl ? (
        <img src={avatarUrl} alt="" referrerPolicy="no-referrer" className={discClass} />
      ) : (
        <div className={`${discClass} flex items-center justify-center`} aria-hidden>
          <span className="font-display font-bold text-[11px] leading-none text-theme-text">{initial}</span>
        </div>
      )}
    </span>
  );
}

function NavLinkBlock({ item, collapsed, onPick, closeMobile }) {
  const location = useLocation();
  const active = navItemIsActive(location.pathname, item.path);

  const inner = (
    <>
      <span className={sidebarNavIconSlotClass}>
        <span className="material-symbols-outlined text-[18px] leading-none">{item.icon}</span>
      </span>
      <span className={`truncate ${collapsed ? "md:hidden" : ""}`}>{item.label}</span>
    </>
  );

  return (
    <Link
      to={item.path}
      onClick={() => {
        closeMobile?.();
        onPick?.();
      }}
      title={collapsed ? item.label : undefined}
      className={`relative flex items-center gap-3 rounded px-3 py-2.5 font-mono text-[12px] tracking-[0.04em] uppercase transition-colors ${
        active
          ? "bg-theme-panel text-theme-accent border-l-2 border-theme-accent"
          : "text-theme-muted hover:bg-theme-panel hover:text-theme-text"
      } ${collapsed ? "md:justify-center md:px-2" : ""}`}
    >
      {inner}
    </Link>
  );
}

/**
 * Shared sidebar markup for desktop column and mobile drawer.
 */
export default function Sidebar({
  currentUser,
  collapsed,
  onCollapsedChange,
  variant = "desktop",
  onCloseMobile,
}) {
  const closeMobile = variant === "mobile" ? onCloseMobile : undefined;
  const mailboxScope = useMailboxScope();

  const shellClass =
    variant === "mobile"
      ? "flex flex-col h-full min-h-0 bg-theme-main border-r border-theme-border w-64 overflow-visible"
      : `hidden md:flex flex-col h-screen sticky top-0 border-r border-theme-border bg-theme-main transition-[width] duration-300 ease-out shrink-0 overflow-visible ${
          collapsed ? "w-[4.5rem]" : "w-60"
        }`;

  return (
    <aside className={shellClass}>
      <div className="flex flex-col flex-1 min-h-0 overflow-visible">
        <div className={`border-b border-theme-border shrink-0 py-6 px-5 ${collapsed ? "md:px-2 md:py-5" : ""}`}>
          {collapsed ? (
            <div className="hidden md:flex justify-center">
              <img src="/icon-192.png" alt="Flipmail" className="h-8 w-8" width={32} height={32} />
            </div>
          ) : null}
          <div className={collapsed ? "md:hidden" : ""}>
            <div className="flex items-center gap-3">
              <img src="/icon-192.png" alt="" className="h-8 w-8 shrink-0" width={32} height={32} />
              <h1 className="font-display font-bold italic text-[28px] leading-[1] text-theme-on-background">Flipmail</h1>
            </div>
            <div className="mt-2 font-mono text-[10px] tracking-[0.14em] uppercase text-theme-dim">
              Confident Rebel Outbound
            </div>
          </div>
        </div>

        <div className={`px-4 py-4 shrink-0 ${collapsed ? "md:px-2" : ""}`}>
          {mailboxScope ? (
            <div className="space-y-1">
              <label
                className={`font-mono text-[10px] uppercase tracking-[0.1em] text-theme-text opacity-60 block ${
                  collapsed ? "md:sr-only" : ""
                }`}
                htmlFor={variant === "mobile" ? "sidebar-mailbox-mobile" : "sidebar-mailbox-desktop"}
              >
                Mailbox
              </label>
              <MailboxDropdown
                id={variant === "mobile" ? "sidebar-mailbox-mobile" : "sidebar-mailbox-desktop"}
                mailboxes={mailboxScope.mailboxes}
                value={mailboxScope.mailboxHash}
                onChange={mailboxScope.setMailboxHash}
                disabled={mailboxScope.mailboxesLoading}
                collapsed={collapsed && variant === "desktop"}
              />
            </div>
          ) : null}
        </div>

        <nav className={`px-3 space-y-0.5 overflow-y-auto flex-1 min-h-0 ${collapsed ? "md:px-1.5" : ""}`}>
          {primaryNavItems.map((item) => (
            <NavLinkBlock key={item.path} item={item} collapsed={collapsed} closeMobile={closeMobile} />
          ))}
        </nav>

        <div className={`mt-auto border-t border-theme-border px-4 py-3 space-y-2 shrink-0 overflow-visible ${collapsed ? "md:px-2" : ""}`}>
          <div
            title={currentUser?.identity?.email || undefined}
            className={`flex items-center gap-3 min-w-0 px-3 ${collapsed && variant === "desktop" ? "md:justify-center md:px-2" : ""}`}
          >
            <SidebarUserAvatar currentUser={currentUser} />
            <div className={`min-w-0 flex-1 ${collapsed ? "md:hidden" : ""}`}>
              <div className="font-mono text-[12px] text-theme-text truncate">
                {currentUser?.identity?.email || "user@flipmail.ai"}
              </div>
              <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-theme-dim capitalize">
                {currentUser?.features?.billing?.plan || "free"} plan
              </div>
            </div>
          </div>

          <div className="space-y-0.5">
            {sidebarAccountNavItems.map((item) => (
              <NavLinkBlock key={item.path} item={item} collapsed={collapsed} closeMobile={closeMobile} />
            ))}
          </div>

          <MoreMenu collapsed={collapsed} variant={variant} onCollapsedChange={onCollapsedChange} />
        </div>
      </div>
    </aside>
  );
}
